import { AnyScale, Coordinates, ScaleValue, ScaleWithBandwidth, TicksSpec } from '../../types';

const getScaleTicks = (scale: AnyScale, spec?: TicksSpec) => {
  // specific values
  if (Array.isArray(spec)) {
    return spec;
  }

  // continuous scales
  if ('ticks' in scale) {
    // default behaviour
    if (spec === undefined) {
      return scale.ticks();
    }

    // specific tick count
    if (typeof spec === 'number' && isFinite(spec) && Math.floor(spec) === spec) {
      return scale.ticks(spec);
    }

    if (typeof spec === 'function') {
      return scale.ticks();
    }
  }

  // non linear scale default
  return scale.domain();
};

export const centerScale = (scale: ScaleWithBandwidth) => {
  const bandwidth = scale.bandwidth();

  if (bandwidth === 0) return scale;

  let offset = bandwidth / 2;
  if (scale.round()) {
    offset = Math.round(offset);
  }

  return <T extends ScaleValue>(d: T) => (scale(d) ?? 0) + offset;
};

export const computeTicks = ({
  axis,
  scale,
  ticksPosition,
  tickValues,
  tickSize,
  tickPadding,
  tickRotation,
  tickRemodelling,
}: {
  axis: 'x' | 'y';
  scale: AnyScale;
  ticksPosition?: 'after' | 'before';
  tickValues?: TicksSpec;
  tickSize: number;
  tickPadding: number;
  tickRotation: number;
  tickRemodelling?: (tickValues: any[]) => any[];
}) => {
  const values = tickRemodelling ? tickRemodelling(getScaleTicks(scale, tickValues)) : getScaleTicks(scale, tickValues);

  const position = 'bandwidth' in scale ? centerScale(scale) : scale;
  const line = { lineX: 0, lineY: 0 };
  const text = { textX: 0, textY: 0 };

  const isRTL = typeof document === 'object' ? document.dir === 'rtl' : false;
  let translate: (value: ScaleValue) => Coordinates;
  let textAlign = 'middle';
  let textBaseline = 'central';

  if (axis === 'x') {
    translate = (d) => ({ x: position(d) ?? 0, y: 0 });

    line.lineY = tickSize * (ticksPosition === 'after' ? 1 : -1);
    text.textY = (tickSize + tickPadding) * (ticksPosition === 'after' ? 1 : -1);

    if (ticksPosition === 'after') {
      textBaseline = 'text-before-edge';
    } else {
      textBaseline = 'alphabetic';
    }

    if (tickRotation === 0) {
      textAlign = 'middle';
    } else if ((ticksPosition === 'after' && tickRotation < 0) || (ticksPosition === 'before' && tickRotation > 0)) {
      textAlign = isRTL ? 'start' : 'end';
      textBaseline = 'middle';
    } else if ((ticksPosition === 'after' && tickRotation > 0) || (ticksPosition === 'before' && tickRotation < 0)) {
      textAlign = isRTL ? 'end' : 'start';
      textBaseline = 'middle';
    }
  } else {
    translate = (d) => ({ x: 0, y: position(d) ?? 0 });

    line.lineX = tickSize * (ticksPosition === 'after' ? 1 : -1);
    text.textX = (tickSize + tickPadding) * (ticksPosition === 'after' ? 1 : -1);

    if (ticksPosition === 'after') {
      textAlign = 'start';
    } else {
      textAlign = 'end';
    }
  }

  const ticks = values.map((value: ScaleValue) => ({
    key: value instanceof Date ? `${value.valueOf()}` : `${value}`,
    value,
    ...translate(value),
    ...line,
    ...text,
  }));

  return {
    ticks,
    textAlign,
    textBaseline,
  };
};
